import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { TrendingUp, Users, Building2, RefreshCw, BarChart3, Globe, Key, Activity, Zap } from 'lucide-react';

const API = (process.env.REACT_APP_BACKEND_URL || '') + '/api/superadmin';

// Semplice barra orizzontale CSS
function Bar({ value, max, color = '#c9a84c' }) {
  const pct = max > 0 ? Math.round((value / max) * 100) : 0;
  return (
    <div className="flex items-center gap-3">
      <div className="flex-1 rounded-full h-1.5" style={{ background: 'rgba(255,255,255,0.06)' }}>
        <div className="h-1.5 rounded-full transition-all" style={{ width: `${pct}%`, background: color }} />
      </div>
      <span className="text-xs w-8 text-right shrink-0" style={{ color: 'var(--lux-text-muted)' }}>{pct}%</span>
    </div>
  );
}

// Sparkline CSS-based (fake ma visiva) dai dati reali
function Sparkline({ data, color = '#c9a84c' }) {
  if (!data || data.length === 0) return null;
  const max = Math.max(...data, 1);
  return (
    <div className="flex items-end gap-0.5 h-8">
      {data.map((v, i) => (
        <div key={i} className="flex-1 rounded-sm transition-all"
          style={{ height: `${Math.max(4, (v / max) * 100)}%`, background: color, opacity: i === data.length - 1 ? 1 : 0.4 }} />
      ))}
    </div>
  );
}

export function AnalyticsTab({ storedKey }) {
  const [stats, setStats] = useState(null);
  const [leads, setLeads] = useState([]);
  const [apiUsage, setApiUsage] = useState([]);
  const [loading, setLoading] = useState(true);

  const headers = { 'X-Superadmin-Key': storedKey };

  useEffect(() => { load(); }, []);

  const load = async () => {
    setLoading(true);
    try {
      const [s, l] = await Promise.all([
        axios.get(`${API}/stats`, { headers }),
        axios.get(`${API}/leads`, { headers }),
      ]);
      setStats(s.data);
      setLeads(l.data.leads || []);

      // API usage: aggrega da master DB (se disponibile)
      try {
        const u = await axios.get(`${API}/api-usage`, { headers });
        setApiUsage(u.data.daily || []);
      } catch {
        setApiUsage([]);
      }
    } catch {
      // silently fail
    } finally {
      setLoading(false);
    }
  };

  if (loading) return (
    <div className="text-center py-20" style={{ color: 'var(--lux-text-muted)' }}>
      <div className="w-6 h-6 border-2 rounded-full animate-spin mx-auto mb-3"
        style={{ borderColor: 'var(--lux-gold)', borderTopColor: 'transparent' }} />
      <p className="text-sm">Caricamento analytics...</p>
    </div>
  );

  // ── Calcoli derivati ────────────────────────────────────────────────────
  const totalLeads = leads.length;
  const demoLeads = leads.filter(l => l.demo_slug).length;
  const convRate = totalLeads > 0 ? Math.round((demoLeads / totalLeads) * 100) : 0;
  const activeLeads = leads.filter(l => l.demo_slug && !l.demo_expired).length;
  const expiredLeads = leads.filter(l => l.demo_slug && l.demo_expired).length;

  // Lead per piano
  const planDist = leads.reduce((acc, l) => {
    const p = l.plan_interest || 'professional';
    acc[p] = (acc[p] || 0) + 1;
    return acc;
  }, {});

  // Lead ultimi 7 giorni (per sparkline)
  const now = new Date();
  const last7 = Array.from({ length: 7 }, (_, i) => {
    const d = new Date(now);
    d.setDate(d.getDate() - (6 - i));
    const dateStr = d.toISOString().slice(0, 10);
    return leads.filter(l => l.created_at && l.created_at.startsWith(dateStr)).length;
  });

  // Tenant per piano
  const planDistribution = stats?.plans_distribution || {};
  const totalTenants = stats?.total_tenants || 0;

  const PLAN_COLORS = { essenziale: '#6b7280', professional: '#c9a84c', elite: '#7c3aed', lifetime: '#059669' };
  const PLAN_LABELS = { essenziale: 'Essenziale', professional: 'Professional', elite: 'Elite', lifetime: 'Lifetime' };

  return (
    <div data-testid="analytics-tab">
      <div className="flex items-center justify-between mb-8">
        <div>
          <h2 className="font-serif text-2xl font-light" style={{ color: 'var(--lux-text)' }}>Analytics</h2>
          <p className="text-xs mt-0.5" style={{ color: 'var(--lux-text-muted)' }}>Panoramica piattaforma in tempo reale</p>
        </div>
        <button onClick={load} className="flex items-center gap-1.5 text-xs px-3 py-2 rounded"
          style={{ border: '1px solid var(--lux-border-2)', color: 'var(--lux-text-muted)' }}>
          <RefreshCw size={13} /> Aggiorna
        </button>
      </div>

      {/* KPI cards row */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-8">
        {[
          { label: 'Tenant Attivi', value: stats?.active_tenants || 0, sub: `di ${totalTenants} totali`, icon: Building2, color: 'var(--lux-gold)' },
          { label: 'Lead Totali', value: totalLeads, sub: `${demoLeads} con demo`, icon: Users, color: '#6366f1' },
          { label: 'Conversione Demo', value: `${convRate}%`, sub: `${activeLeads} demo attive ora`, icon: TrendingUp, color: '#10b981' },
          { label: 'Prenotazioni', value: stats?.total_bookings_platform || 0, sub: 'su tutta la piattaforma', icon: BarChart3, color: '#a78bfa' },
        ].map(({ label, value, sub, icon: Icon, color }) => (
          <div key={label} className="p-5 rounded-sm" style={{ background: 'var(--lux-surface-1)', border: '1px solid var(--lux-border-1)' }}>
            <div className="flex items-center gap-2 mb-3">
              <Icon size={13} style={{ color }} />
              <p className="text-[10px] uppercase tracking-widest" style={{ color: 'var(--lux-text-muted)' }}>{label}</p>
            </div>
            <p className="font-serif text-3xl font-light mb-0.5" style={{ color: 'var(--lux-text)' }}>{value}</p>
            <p className="text-xs" style={{ color: 'var(--lux-text-muted-3)' }}>{sub}</p>
          </div>
        ))}
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-6">
        {/* Lead trend 7 giorni */}
        <div className="p-5 rounded-sm" style={{ background: 'var(--lux-surface-1)', border: '1px solid var(--lux-border-1)' }}>
          <div className="flex items-center justify-between mb-4">
            <div>
              <p className="text-xs uppercase tracking-widest mb-0.5" style={{ color: 'var(--lux-gold)' }}>Lead — Ultimi 7 giorni</p>
              <p className="text-xs" style={{ color: 'var(--lux-text-muted)' }}>Nuovi lead per giorno</p>
            </div>
            <span className="font-serif text-2xl font-light" style={{ color: 'var(--lux-text)' }}>
              {last7.reduce((a, b) => a + b, 0)}
            </span>
          </div>
          <Sparkline data={last7} color="#c9a84c" />
          <div className="flex justify-between mt-2">
            {['L-6', 'L-5', 'L-4', 'L-3', 'L-2', 'Ieri', 'Oggi'].map(d => (
              <span key={d} className="text-[10px]" style={{ color: 'var(--lux-text-muted-3)' }}>{d}</span>
            ))}
          </div>
        </div>

        {/* Piano distribuzione lead */}
        <div className="p-5 rounded-sm" style={{ background: 'var(--lux-surface-1)', border: '1px solid var(--lux-border-1)' }}>
          <p className="text-xs uppercase tracking-widest mb-5" style={{ color: 'var(--lux-gold)' }}>Lead per piano di interesse</p>
          <div className="space-y-4">
            {Object.entries(PLAN_LABELS).map(([key, label]) => {
              const count = planDist[key] || 0;
              return (
                <div key={key}>
                  <div className="flex items-center justify-between mb-1.5">
                    <span className="text-xs" style={{ color: 'var(--lux-text-sec)' }}>{label}</span>
                    <span className="text-xs font-medium" style={{ color: PLAN_COLORS[key] }}>{count}</span>
                  </div>
                  <Bar value={count} max={totalLeads} color={PLAN_COLORS[key]} />
                </div>
              );
            })}
          </div>
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-6">
        {/* Tenant per piano */}
        <div className="p-5 rounded-sm" style={{ background: 'var(--lux-surface-1)', border: '1px solid var(--lux-border-1)' }}>
          <p className="text-xs uppercase tracking-widest mb-5" style={{ color: 'var(--lux-gold)' }}>Distribuzione Tenant per Piano</p>
          {Object.entries(planDistribution).length > 0 ? (
            <div className="space-y-4">
              {Object.entries(planDistribution).map(([plan, count]) => (
                <div key={plan}>
                  <div className="flex items-center justify-between mb-1.5">
                    <div className="flex items-center gap-2">
                      <div className="w-2 h-2 rounded-full" style={{ background: PLAN_COLORS[plan] || '#888' }} />
                      <span className="text-xs" style={{ color: 'var(--lux-text-sec)' }}>{PLAN_LABELS[plan] || plan}</span>
                    </div>
                    <span className="text-xs font-medium" style={{ color: PLAN_COLORS[plan] || '#888' }}>{count}</span>
                  </div>
                  <Bar value={count} max={totalTenants} color={PLAN_COLORS[plan] || '#888'} />
                </div>
              ))}
            </div>
          ) : (
            <p className="text-sm" style={{ color: 'var(--lux-text-muted)' }}>Nessun tenant attivo.</p>
          )}
        </div>

        {/* Demo funnel */}
        <div className="p-5 rounded-sm" style={{ background: 'var(--lux-surface-1)', border: '1px solid var(--lux-border-1)' }}>
          <p className="text-xs uppercase tracking-widest mb-5" style={{ color: 'var(--lux-gold)' }}>Funnel Demo</p>
          <div className="space-y-5">
            {[
              { label: 'Lead totali', value: totalLeads, color: '#6366f1', pct: 100 },
              { label: 'Hanno richiesto demo', value: demoLeads, color: '#c9a84c', pct: totalLeads > 0 ? Math.round(demoLeads / totalLeads * 100) : 0 },
              { label: 'Demo attive ora', value: activeLeads, color: '#10b981', pct: totalLeads > 0 ? Math.round(activeLeads / totalLeads * 100) : 0 },
              { label: 'Demo scadute', value: expiredLeads, color: '#6b7280', pct: totalLeads > 0 ? Math.round(expiredLeads / totalLeads * 100) : 0 },
            ].map(({ label, value, color, pct }) => (
              <div key={label}>
                <div className="flex items-center justify-between mb-1.5">
                  <span className="text-xs" style={{ color: 'var(--lux-text-sec)' }}>{label}</span>
                  <div className="flex items-center gap-2">
                    <span className="text-xs font-medium" style={{ color }}>{value}</span>
                    <span className="text-[10px]" style={{ color: 'var(--lux-text-muted-3)' }}>{pct}%</span>
                  </div>
                </div>
                <div className="h-1.5 rounded-full" style={{ background: 'rgba(255,255,255,0.06)' }}>
                  <div className="h-1.5 rounded-full" style={{ width: `${pct}%`, background: color }} />
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* API Usage overview */}
      <div className="p-5 rounded-sm" style={{ background: 'var(--lux-surface-1)', border: '1px solid var(--lux-border-1)' }}>
        <div className="flex items-center gap-2 mb-5">
          <Zap size={14} style={{ color: 'var(--lux-gold)' }} />
          <p className="text-xs uppercase tracking-widest" style={{ color: 'var(--lux-gold)' }}>API Pubblica — Utilizzo</p>
        </div>
        {apiUsage.length > 0 ? (
          <div>
            <Sparkline data={apiUsage} color="#6366f1" />
            <p className="text-xs mt-2" style={{ color: 'var(--lux-text-muted)' }}>Richieste API totali negli ultimi 7 giorni</p>
          </div>
        ) : (
          <div className="flex items-center gap-3 py-4">
            <Activity size={16} style={{ color: 'var(--lux-text-muted-3)' }} />
            <p className="text-sm" style={{ color: 'var(--lux-text-muted)' }}>
              Nessun utilizzo API registrato ancora. I dati appariranno quando i tenant inizieranno ad usare le API pubbliche.
            </p>
          </div>
        )}
      </div>
    </div>
  );
}
